import { IAuthRoles, IAuthUser } from "@auth/firebase/interfaces"
import { CalendarId, calendarIdAtom } from "core/calendar/state"
import { HttpError, useCreate, useGetIdentity, useInvalidate, useList, usePermissions, useUpdate } from "@refinedev/core"
import { PRO_ROLES, USER_ROLES } from "access/definitions"
import { DEFAULT_CALENDAR_APPOINTMENTS_NAME, DEFAULT_CALENDAR_AVAILABILITY_NAME } from "core/calendar/definitions"
import { useAtom } from "jotai"
import { useEffect, useRef, useCallback, useMemo } from "react"
import { calendar_v3 } from "googleapis"
import { Professional } from "@graphql/schema.types"
import { logger } from "@utils/logger"

export const useAppCalendar = () => {
    const invalidate = useInvalidate();
    const [calendarId, setCalendarId] = useAtom(calendarIdAtom)
    const { data: roleData } = usePermissions<IAuthRoles>()
    const role = roleData?.role
    const { data: identity } = useGetIdentity<IAuthUser>();
    
    // Use ref to track initialization state instead of global singleton
    const isInitializing = useRef(false)

    const { data: calendarList, isLoading, isError } = useList<calendar_v3.Schema$CalendarListEntry>({
        dataProviderName: 'gapi',
        resource: 'calendar/users/me/calendarList',
        pagination: {
            pageSize: 50
        }
    })
    // logger.debug("calendarList", calendarList)

    const { mutateAsync: createCalendar } = useCreate<calendar_v3.Schema$Calendar, HttpError, calendar_v3.Schema$Calendar>({
        dataProviderName: 'gapi',
        resource: 'calendar/calendars',
        successNotification: false,
    })
    const { mutateAsync: createAcl } = useCreate<calendar_v3.Schema$AclRule, HttpError, calendar_v3.Schema$AclRule>({
        dataProviderName: 'gapi',
        resource: 'calendar/calendars/:calendar/acl',
        successNotification: false,
    })
    const { mutateAsync: registerCalendar } = useUpdate<Professional, HttpError, Partial<Professional>>({
        resource: 'professional',
        successNotification: false,
    })

    // Memoize invalidateCalendar function
    const invalidateCalendar = useCallback(async () => {
        await invalidate({
            dataProviderName: 'gapi',
            resource: 'calendar/users/me/calendarList',
            invalidates: ['list']
        })
    }, [invalidate])

    // Memoize selectCalendar function
    const selectCalendar = useCallback(async (): Promise<CalendarId> => {
        // logger.debug(names)
        if (USER_ROLES.includes(role)) {
            return { appointments: "primary" } //TODO: let the user choose the calendar
        } else if (PRO_ROLES.includes(role)) {
            let appointmentsCalendar = calendarList?.data.find(c => c.summary == DEFAULT_CALENDAR_APPOINTMENTS_NAME)?.id
            let availabilityCalendar = calendarList?.data.find(c => c.summary == DEFAULT_CALENDAR_AVAILABILITY_NAME)?.id

            if (!appointmentsCalendar) {
                const createCalendarAppointmentsRes = await createCalendar({
                    values: {
                        summary: DEFAULT_CALENDAR_APPOINTMENTS_NAME
                    }
                })
                appointmentsCalendar = createCalendarAppointmentsRes.data.id!
                await invalidateCalendar()
            }

            if (!availabilityCalendar) {
                const createCalendarAvailabilityRes = await createCalendar({
                    values: {
                        summary: DEFAULT_CALENDAR_AVAILABILITY_NAME
                    }
                })
                availabilityCalendar = createCalendarAvailabilityRes.data.id!
                await invalidateCalendar()
            }

            const bootstrapCalendars = async () => {
                await registerCalendar({
                    id: identity?.user_id,
                    values: {
                        appointments_calendar_id: appointmentsCalendar,
                        availability_calendar_id: availabilityCalendar
                    },
                })

                await createAcl({
                    values: {
                        role: "freeBusyReader",
                        scope: {
                            type: "default",
                            value: ''
                        }
                    },
                    meta: {
                        params: {
                            calendar: availabilityCalendar
                        }
                    }
                })

                await createAcl({
                    values: {
                        role: "freeBusyReader",
                        scope: {
                            type: "default",
                            value: ''
                        }
                    },
                    meta: {
                        params: {
                            calendar: appointmentsCalendar
                        }
                    }
                })
            }

            setTimeout(bootstrapCalendars, 10)

            return {
                appointments: appointmentsCalendar,
                availability: availabilityCalendar,
            }

        }

        return {
            appointments: "primary"
        }
    }, [role, calendarList?.data, createCalendar, createAcl, registerCalendar, invalidateCalendar, identity?.user_id])

    // Memoize the initialization condition
    const shouldInitialize = useMemo(() => {
        return !isInitializing.current && !calendarId?.appointments && calendarList && !isLoading
    }, [calendarId?.appointments, calendarList, isLoading])

    // get the default app calendar based in name (summary)
    useEffect(() => {
        if (shouldInitialize) {
            isInitializing.current = true
            logger.debug("check events", calendarId, isLoading, calendarList)
            
            const initializeCalendar = async () => {
                try {
                    const result = await selectCalendar()
                    setCalendarId(result)
                } catch (err) {
                    logger.error("Failed to initialize calendar:", err)
                    isInitializing.current = false
                }
            }
            
            initializeCalendar()
        }
    }, [shouldInitialize, selectCalendar, setCalendarId])

    return calendarId
}
